import type { ConsentableNotificationChannel } from './notification.js';

/**
 * Who a donation may tell about itself — the four parties *Bildirim İşlemleri*
 * offers (GitHub issue #2 §5.2): the person who paid, *Bağış Sahibi* — the
 * person the gift is made on behalf of, when the donation names them as a
 * donor (docs/decisions/0079) — the person who introduced them, and the member
 * of staff who typed the record. The database enum is still called
 * `donation_evidence_recipient_role`, from when evidence was the only notice
 * chosen per party; the list is closed because each party is reached in its
 * own way — docs/decisions/0070 and 0080.
 */
export const DONATION_EVIDENCE_RECIPIENT_ROLES = [
  'payer',
  'owner',
  'reference',
  'operator',
] as const;
export type DonationEvidenceRecipientRole = (typeof DONATION_EVIDENCE_RECIPIENT_ROLES)[number];

/**
 * **The two notices staff choose per party**, one column each on the entry
 * form: `registration` — *KAYIT BİLDİRİMİ*, told once, when the donation is
 * saved — and `fulfilment` — *GERÇEKLEŞTİ (GÖRSEL/KANIT) BİLDİRİMİ*, the
 * expiring evidence link of docs/decisions/0075, told when the donation is
 * carried out with evidence to show. A choice is a row per donation, party and
 * notice — docs/decisions/0080.
 */
export const DONATION_NOTICE_KINDS = ['registration', 'fulfilment'] as const;
export type DonationNoticeKind = (typeof DONATION_NOTICE_KINDS)[number];

/**
 * What each party's notices rest on. Since docs/decisions/0080 the payer and
 * *Bağış Sahibi* are told **because staff ticked them**, about a donation that
 * is theirs: a transactional message, sent without asking the donor's
 * per-channel consent (docs/decisions/0061), which governs everything else the
 * organization sends a donor. The reference person rests on the operator's
 * declaration, as before (docs/decisions/0070), and the operator on being
 * staff.
 */
export const EVIDENCE_DELIVERY_BASIS_VALUES = [
  'transactional_notice',
  'operator_declaration',
  'staff_membership',
] as const;

export type EvidenceDeliveryBasis = (typeof EVIDENCE_DELIVERY_BASIS_VALUES)[number];

export const EVIDENCE_DELIVERY_BASES = {
  payer: 'transactional_notice',
  owner: 'transactional_notice',
  reference: 'operator_declaration',
  operator: 'staff_membership',
} as const satisfies Record<DonationEvidenceRecipientRole, EvidenceDeliveryBasis>;

/**
 * The channel a choice takes when the entry form names none. Every party is
 * reached at a telephone — the donor's, the reference's, the user's own
 * (migration `0107`) — and the evidence already reaches this organization over
 * WhatsApp (docs/plans/05-finance-reconciliation.md §5.9), so that is the
 * channel it leaves on.
 */
export const DEFAULT_EVIDENCE_DELIVERY_CHANNEL: ConsentableNotificationChannel = 'whatsapp';

/**
 * *Varsayılan Durum*: the payer has both notices ticked, every other party
 * none — the choices a staff entry takes when it names none (docs/decisions/0080).
 */
export const DEFAULT_NOTICE_CHOICES: readonly {
  role: DonationEvidenceRecipientRole;
  kind: DonationNoticeKind;
}[] = [
  { role: 'payer', kind: 'registration' },
  { role: 'payer', kind: 'fulfilment' },
];

/**
 * Whether a chosen notice may still leave on one party's row: only while the
 * party has not opted out, from the staff screen or from their own evidence
 * link's page. Withdrawing must be no harder than the tick that asked. What it
 * no longer reads is the donor's channel consent — docs/decisions/0080 — and an
 * erased donor is not asked here at all: the erasure removes the rows.
 */
export function mayDeliverEvidence(input: { optedOutAt: Date | null }): boolean {
  return input.optedOutAt === null;
}

/**
 * Why an evidence link stopped opening. `opted_out` is the party saying they
 * want no more — from the staff screen or from the link's own page;
 * `erased` is the donor's erasure (docs/decisions/0062), which leaves nobody
 * the evidence could lawfully be about; `recipient_removed` is the preference
 * row going by any other path. A link is never un-revoked.
 */
export const EVIDENCE_LINK_REVOCATION_REASONS = [
  'opted_out',
  'erased',
  'recipient_removed',
] as const;
export type EvidenceLinkRevocationReason = (typeof EVIDENCE_LINK_REVOCATION_REASONS)[number];

/**
 * How long an evidence link opens after it is sent: the owner's thirty days
 * unless `EVIDENCE_LINK_TTL_DAYS` says otherwise, and never past a year —
 * `donation_evidence_links_issue_paired` refuses a longer life at the
 * database, whatever the configuration read.
 */
export const EVIDENCE_LINK_DEFAULT_TTL_DAYS = 30;
export const EVIDENCE_LINK_MAX_TTL_DAYS = 365;

/**
 * The four events that carry an evidence link, one per party, each
 * addressed as its party is — docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
 */
export const EVIDENCE_NOTIFICATION_EVENTS = {
  payer: 'donation.evidence_ready',
  owner: 'donation.evidence_ready_owner',
  reference: 'donation.evidence_ready_reference',
  operator: 'donation.evidence_ready_operator',
} as const satisfies Record<DonationEvidenceRecipientRole, string>;

/**
 * *KAYIT BİLDİRİMİ*'s event for each party but the payer, whose event is
 * `donation.recorded` — or `donation.request_recorded` for a donation that took
 * no money — chosen by the donation's own payment axis (docs/decisions/0073).
 */
export const REGISTRATION_NOTIFICATION_EVENTS = {
  owner: 'donation.recorded_owner',
  reference: 'donation.recorded_reference',
  operator: 'donation.recorded_operator',
} as const satisfies Record<Exclude<DonationEvidenceRecipientRole, 'payer'>, string>;

export function isEvidenceNotificationEvent(event: string): boolean {
  return (Object.values(EVIDENCE_NOTIFICATION_EVENTS) as string[]).includes(event);
}

/**
 * **What an evidence message stores where its link will go.** The link is a
 * token minted when the message actually leaves, handed to the provider and
 * kept only as its hash, so the row — which keeps the Turkish that was sent —
 * keeps this instead, and the delivery puts the link in its place at the last
 * moment. It reads as what it is on the operator's delivery screen.
 */
export const EVIDENCE_LINK_PLACEHOLDER = '[kanıt bağlantısı gönderimde oluşturulur]';
